import React from "react";
import { useTranslation } from "react-i18next";
import { HorizontalSpacer } from "components/styled";
import { DatePicker, IDatePicker } from "@fluentui/react-datepicker-compat";
import { Select } from "@fluentui/react-components";
import { Search } from "pages/BankStatements/BankStatementsGrid/Search";
import { useImportedInvoicesStore } from "store/ImportedInvoicesStore";
import { defaultDatePickerStrings } from "@fluentui/react";
import moment from "moment";
import { isInvalidDate } from "utils/utils";
import { helpAnchor } from "help/anchor";
import { IMPORTED_INVOICE_MATCH_STATES } from "types/importedInvoices";

const onFormatDate = (date?: Date): string => {
  return !date
    ? ""
    : date.getDate() + "." + (date.getMonth() + 1) + "." + date.getFullYear();
};

// first toolbar row: text search (client side), issue date range and match state
// (server side, reload on change)
const ImportedInvoicesMenubar = () => {
  const { t } = useTranslation();
  const [
    {
      searchTerm,
      fromDateFilter,
      toDateFilter,
      matchStateFilter,
      importedInvoicesLoadStatus,
    },
    { setImportedInvoicesStoreValue },
  ] = useImportedInvoicesStore();
  const isLoading = importedInvoicesLoadStatus === "loading";

  const fromDatePickerRef = React.useRef<IDatePicker>(null);
  const toDatePickerRef = React.useRef<IDatePicker>(null);

  const parseDate = (newValue: string, previousValue: Date | null): Date => {
    const previous = previousValue || new Date();
    const parts = (newValue || "").trim().split(".");
    const day =
      parts.length > 0
        ? Math.max(1, Math.min(31, parseInt(parts[0], 10)))
        : previous.getDate();
    const month =
      parts.length > 1
        ? Math.max(1, Math.min(12, parseInt(parts[1], 10))) - 1
        : previous.getMonth();
    const year = parts.length > 2 ? parseInt(parts[2], 10) : previous.getFullYear();
    return new Date(year, month, day);
  };

  const onBlurDatePicker =
    (field: "fromDateFilter" | "toDateFilter", current: Date | null) =>
    (e: any) => {
      const inputEl: any =
        e.currentTarget.childNodes[0].childNodes[0].childNodes[0].childNodes[0];
      setTimeout(() => {
        inputEl.blur();
        const val = parseDate(inputEl.value, current);
        if (
          !isInvalidDate(val) &&
          moment(val.getTime()).format("YYYY-MM-DD") !==
            moment(current?.getTime()).format("YYYY-MM-DD")
        )
          setImportedInvoicesStoreValue(field, isNaN(val.getTime()) ? null : val);
      }, 300);
    };

  return (
    <>
      <Search
        dataHelp={helpAnchor("imported-invoices-search")}
        placeholder="Search"
        value={searchTerm}
        onChange={(value) => setImportedInvoicesStoreValue("searchTerm", value)}
      />{" "}
      <HorizontalSpacer width={10} />
      <DatePicker
        onError={() => {
          return;
        }}
        onErrorCapture={() => {
          return;
        }}
        data-help="imported-invoices-from-date"
        componentRef={fromDatePickerRef}
        allowTextInput
        value={fromDateFilter}
        onSelectDate={(date: Date | undefined) => {
          setImportedInvoicesStoreValue("fromDateFilter", date);
        }}
        parseDateFromString={(dateStr) => parseDate(dateStr, fromDateFilter)}
        strings={defaultDatePickerStrings}
        disableAutoFocus={true}
        formatDate={onFormatDate}
        onBlur={onBlurDatePicker("fromDateFilter", fromDateFilter)}
        placeholder="Select from date..."
      />
      <HorizontalSpacer width={10} />
      <DatePicker
        onError={() => {
          return;
        }}
        onErrorCapture={() => {
          return;
        }}
        data-help="imported-invoices-to-date"
        componentRef={toDatePickerRef}
        allowTextInput
        value={toDateFilter}
        onSelectDate={(date) => {
          setImportedInvoicesStoreValue("toDateFilter", date);
        }}
        parseDateFromString={(dateStr) => parseDate(dateStr, toDateFilter)}
        strings={defaultDatePickerStrings}
        disableAutoFocus={true}
        formatDate={onFormatDate}
        onBlur={onBlurDatePicker("toDateFilter", toDateFilter)}
        placeholder="Select to date..."
      />
      <HorizontalSpacer width={10} />
      <Select
        data-help="imported-invoices-match-state"
        title={t("pct.importedInvoiceMatchState.label")}
        value={matchStateFilter}
        disabled={isLoading}
        onChange={(e, data) =>
          setImportedInvoicesStoreValue("matchStateFilter", data.value)
        }
      >
        <option value="">{t("pct.importedInvoiceMatchState.all.label")}</option>
        {IMPORTED_INVOICE_MATCH_STATES.map((state) => (
          <option key={state} value={state}>
            {t("pct.importedInvoiceMatchState." + state + ".label")}
          </option>
        ))}
      </Select>
    </>
  );
};

export default ImportedInvoicesMenubar;
